import React, { useEffect, useState } from "react";
import { 
  Tabs, 
  Tab, 
  Box, 
  Typography,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  TextField,
  Pagination,
  Skeleton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import { 
  Search,
  ArrowUpward,
  ArrowDownward,
  PlayCircleOutline,
  GetApp
} from "@mui/icons-material";
import axios from "axios";
import { styled } from '@mui/material/styles';
import AdminDashboard from "./AdminDashboard";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  fontSize: '0.875rem',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&.MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: 500,
  borderRadius: 4,
  backgroundColor: status === 'pending' 
    ? theme.palette.warning.light
    : theme.palette.success.light,
  color: status === 'pending' 
    ? theme.palette.warning.dark
    : theme.palette.success.dark,
}));

const Earnings = () => {
  const theme = useTheme();
  const [pendingData, setPendingData] = useState([]);
  const [settledData, setSettledData] = useState([]);
  const [currentTab, setCurrentTab] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [openDialog, setOpenDialog] = useState(false);
  const [settlementData, setSettlementData] = useState([]);
  const [settlementLoading, setSettlementLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [settleLoading, setSettleLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const userData = JSON.parse(sessionStorage.getItem("admin_user"));
  const { UserID, AuthCode } = userData.BoAcntLst[0];

  const columns = [
    { id: 'EarningID', label: 'Earning ID', sortable: true },
    { id: 'ReferenceID', label: 'Reference ID', sortable: true },
    { id: 'FullName', label: 'Full Name', sortable: true },
    { id: 'EarningType', label: 'Earning Type', sortable: true },
    { id: 'PVValue', label: 'PV Value', sortable: true },
    { id: 'TotalRate', label: 'Total Amount', sortable: true },
    { id: 'Status', label: 'Status', sortable: false },
  ];

  const settlementColumns = [
    { id: 'checkbox', label: '', sortable: false },
    { id: 'FullName', label: 'Full Name', sortable: true },
    { id: 'ReferralID', label: 'Referral ID', sortable: true },
    { id: 'Amount', label: 'Amount', sortable: true },
    { id: 'BankName', label: 'Bank Name', sortable: true },
    { id: 'BankBranch', label: 'Bank Branch', sortable: true },
    { id: 'AcName', label: 'Account Name', sortable: true },
    { id: 'AcNo', label: 'Account Number', sortable: true },
    { id: 'AcType', label: 'Account Type', sortable: true },
  ];

  // Fetch data based on current tab
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const requestBody = {
          ComID: "EH25",
          UserID: UserID,
          Flag: currentTab === "pending" ? "PE" : "SE", 
          PVType: "-1",
          ReferenceID: "-1",
          FromDate: "2024-06-01",
          ToDate: new Date().toISOString().split('T')[0], // Current date
          AuthCode: AuthCode
        };

        const response = await axios.post(
          "https://testing.esnep.com/magnessa/api/bo/earning",
          requestBody,
          { headers: { "Content-Type": "application/json" } }
        );

        if (response.data?.BoProdLst) {
          if (currentTab === "pending") {
            setPendingData(response.data.BoProdLst);
          } else {
            setSettledData(response.data.BoProdLst);
          }
        }
      } catch (error) {
        console.error("Error fetching earnings data:", error);
        showSnackbar('Error fetching earnings data', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentTab, UserID, AuthCode]);

  const handleRunCommission = async () => {
    setSettlementLoading(true);
    setSelectedRows([]); // Reset selected rows when running new commission
    try {
      const requestBody = {
        ComID: "EH25",
        UserID: UserID,
        Flag: "GE",
        AuthCode: AuthCode
      };

      const response = await axios.post(
        "https://testing.esnep.com/magnessa/api/bo/earning",
        requestBody,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data && response.data.BoProdLst) {
        setSettlementData(response.data.BoProdLst);
        setOpenDialog(true);
        
        // Refresh the settled data after running commission
        const settledResponse = await axios.post(
          "https://testing.esnep.com/magnessa/api/bo/earning",
          {
            ComID: "EH25",
            UserID: UserID,
            Flag: "SE",
            PVType: "-1",
            ReferenceID: "-1",
            FromDate: "2024-06-01",
            ToDate: new Date().toISOString().split('T')[0],
            AuthCode: AuthCode
          },
          { headers: { "Content-Type": "application/json" } }
        );
        
        if (settledResponse.data?.BoProdLst) {
          setSettledData(settledResponse.data.BoProdLst);
        }
        showSnackbar('Commission run successfully', 'success');
      } else {
        console.error("Unexpected response structure:", response.data);
        showSnackbar(response.data?.Message || 'Unexpected response from server', 'error');
      }
    } catch (error) {
      console.error("Error running commission:", error);
      showSnackbar(error.response?.data?.Message || 'Error running commission', 'error');
    } finally {
      setSettlementLoading(false);
    }
  };

  const handleSelectAllRows = (event) => {
    if (event.target.checked) {
      const newSelected = settlementData.map((row, index) => index);
      setSelectedRows(newSelected);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (event, index) => {
    const selectedIndex = selectedRows.indexOf(index);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedRows, index);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedRows.slice(1));
    } else if (selectedIndex === selectedRows.length - 1) {
      newSelected = newSelected.concat(selectedRows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedRows.slice(0, selectedIndex),
        selectedRows.slice(selectedIndex + 1)
      );
    }

    setSelectedRows(newSelected);
  };

  const isSelected = (index) => selectedRows.indexOf(index) !== -1;

  const handleSettleEarnings = async () => {
    setSettleLoading(true);
    try {
      // Get the selected ReferralIDs from the settlementData based on selectedRows
      const selectedReferrals = selectedRows.map(index => ({
        ReferralID: settlementData[index].ReferralID
      }));

      const requestBody = {
        ComID: "EH25",
        UserID: UserID,
        Flag: "SEE",
        Referral: selectedReferrals, // Include the selected ReferralIDs
        AuthCode: AuthCode
      };

      const response = await axios.post(
        "https://testing.esnep.com/magnessa/api/bo/setearning",
        requestBody,
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data) {
        // Refresh the settled data after settling earnings
        const settledResponse = await axios.post(
          "https://testing.esnep.com/magnessa/api/bo/earning",
          {
            ComID: "EH25",
            UserID: UserID,
            Flag: "SE",
            PVType: "-1",
            ReferenceID: "-1",
            FromDate: "2024-06-01",
            ToDate: new Date().toISOString().split('T')[0],
            AuthCode: AuthCode
          },
          { headers: { "Content-Type": "application/json" } }
        );
        
        if (settledResponse.data?.BoProdLst) {
        setSettledData(settledResponse.data.BoProdLst);
      }

      setOpenDialog(false);
      setSelectedRows([]);
      showSnackbar(response.data?.Message || 'Earnings settled successfully', 'success');
    } else {
      showSnackbar(response.data?.Message || 'Failed to settle earnings', 'error');
    }
  } catch (error) {
    console.error("Error settling earnings:", error);
    showSnackbar(error.response?.data?.Message || 'Error settling earnings', 'error');
  } finally {
    setSettleLoading(false);
  }
};

  const exportToCSV = () => {
    if (settlementData.length === 0) return;
    
    // Create CSV headers
    const headers = settlementColumns.filter(col => col.id !== 'checkbox').map(col => col.label).join(',');
    
    // Create CSV rows
    const rows = settlementData.map(item => 
      settlementColumns.filter(col => col.id !== 'checkbox').map(col => {
        // Handle Amount formatting
        if (col.id === 'Amount' && item[col.id]) {
          return `"${item[col.id].toLocaleString()}"`;
        }
        return `"${item[col.id] || 'N/A'}"`;
      }).join(',')
    ).join('\n');
    
    // Combine headers and rows
    const csvContent = `${headers}\n${rows}`;
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `commission_settlement_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('CSV exported successfully', 'success');
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleSettlementSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Get the current data based on tab
  const currentData = currentTab === "pending" ? pendingData : settledData;

  const filteredData = currentData.filter(item =>
    item.FullName && item.FullName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortConfig.key) {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const paginatedData = sortedData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <AdminDashboard>
      <Box sx={{ 
        backgroundColor: 'white', 
        borderRadius: 4,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        p: 3
      }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Earnings Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{lineHeight:"2"}}>
              Track and manage pending and settled earnings
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<PlayCircleOutline />}
            onClick={handleRunCommission}
            disabled={settlementLoading || currentTab === "settled"}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              height: '40px'
            }}
          >
            {settlementLoading ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Processing...
              </>
            ) : 'Run Commission'}
          </Button>
        </Box>

        <Paper sx={{ 
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={currentTab}
              onChange={(_, newValue) => {
                setCurrentTab(newValue);
                setPage(1); // Reset pagination when changing tabs
                setSearchTerm(''); // Reset search when changing tabs
              }}
              sx={{
                '& .MuiTabs-indicator': {
                  backgroundColor: theme.palette.primary.main,
                }
              }}
            >
              <Tab label="Pending" value="pending" />
              <Tab label="Settled" value="settled" />
            </Tabs>
          </Box>
          <Box sx={{ 
            p: 2, 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder={`Search ${currentTab} earnings...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search fontSize="small" sx={{ mr: 1 }} />,
                sx: { borderRadius: 2, width: { xs: '100%', sm: 300 } }
              }}
              sx={{ mt: { xs: 2, sm: 0 } }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <StyledTableCell 
                      key={column.id}
                      onClick={() => column.sortable && handleSort(column.id)}
                      sx={{ 
                        cursor: column.sortable ? 'pointer' : 'default',
                        minWidth: 150,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <span>{column.label}</span>
                        {column.sortable && sortConfig.key === column.id && (
                          sortConfig.direction === 'asc' ? 
                          <ArrowUpward fontSize="small" sx={{ fontSize: '0.75rem' }} /> : 
                          <ArrowDownward fontSize="small" sx={{ fontSize: '0.75rem' }} />
                        )}
                      </Stack>
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      {columns.map((_, i) => (
                        <StyledTableCell key={i}>
                          <Skeleton variant="text" width={120} />
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  paginatedData.map((row, index) => (
                    <TableRow hover key={row.EarningID || index} sx={{ 
                      backgroundColor: index % 2 === 0 ? 'inherit' : theme.palette.grey[50]
                    }}>
                      <StyledTableCell>{row.EarningID || "N/A"}</StyledTableCell>
                      <StyledTableCell>{row.ReferenceID || "N/A"}</StyledTableCell>
                      <StyledTableCell>{row.FullName || "N/A"}</StyledTableCell>
                      <StyledTableCell>{row.EarningType || "N/A"}</StyledTableCell>
                      <StyledTableCell>{row.PVValue || "N/A"}</StyledTableCell>
                      <StyledTableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography fontWeight={600} color="success.main">
                            {row.TotalRate ? parseFloat(row.TotalRate).toFixed(2) : "N/A"}
                          </Typography>
                        </Stack>
                      </StyledTableCell>
                      <StyledTableCell>
                        <StatusChip 
                          label={currentTab === "pending" ? "Pending" : "Settled"} 
                          status={currentTab}
                        />
                      </StyledTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            borderTop: `1px solid ${theme.palette.divider}`
          }}>
            <Typography variant="body2" color="text.secondary">
              Showing {paginatedData.length} of {filteredData.length} results
            </Typography>
            <Pagination
              count={Math.ceil(filteredData.length / rowsPerPage)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
              shape="rounded"
            />
          </Box>
        </Paper>
      </Box>

      {/* Settlement Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              Commission Settlement Results
            </Typography>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GetApp />}
                onClick={exportToCSV}
                disabled={settlementData.length === 0}
                sx={{ mr: 2 }}
              >
                Export CSV
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSettleEarnings}
                disabled={selectedRows.length === 0 || settleLoading}
              >
                {settleLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Processing...
                  </>
                ) : 'Settle Earnings'}
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper} sx={{ mt: 2, maxHeight: '60vh' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {settlementColumns.map((column) => (
                    <StyledTableCell 
                      key={column.id}
                      onClick={() => column.sortable && handleSettlementSort(column.id)}
                      sx={{ 
                        cursor: column.sortable ? 'pointer' : 'default',
                        minWidth: column.id === 'checkbox' ? 50 : 150,
                      }}
                    >
                      {column.id === 'checkbox' ? (
                        <Checkbox
                          indeterminate={selectedRows.length > 0 && selectedRows.length < settlementData.length}
                          checked={settlementData.length > 0 && selectedRows.length === settlementData.length}
                          onChange={handleSelectAllRows}
                        />
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <span>{column.label}</span>
                          {column.sortable && sortConfig.key === column.id && (
                            sortConfig.direction === 'asc' ? 
                            <ArrowUpward fontSize="small" sx={{ fontSize: '0.75rem' }} /> : 
                            <ArrowDownward fontSize="small" sx={{ fontSize: '0.75rem' }} />
                          )}
                        </Stack>
                      )}
                    </StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {settlementLoading ? (
                  Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={index}>
                      {settlementColumns.map((_, i) => (
                        <StyledTableCell key={i}>
                          <Skeleton variant="text" width={120} />
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  ))
                ) : settlementData.length > 0 ? (
                  settlementData.map((row, index) => {
                    const isItemSelected = isSelected(index);
                    return (
                      <TableRow 
                        hover 
                        key={index}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        selected={isItemSelected}
                      >
                        <StyledTableCell>
                          <Checkbox
                            checked={isItemSelected}
                            onChange={(event) => handleSelectRow(event, index)}
                          />
                        </StyledTableCell>
                        <StyledTableCell>{row.FullName || "N/A"}</StyledTableCell>
                        <StyledTableCell>{row.ReferralID || "N/A"}</StyledTableCell>
                        <StyledTableCell>{row.Amount ? row.Amount.toLocaleString() : "N/A"}</StyledTableCell>
                        <StyledTableCell>{row.BankName || "N/A"}</StyledTableCell>
                        <StyledTableCell>{row.BankBranch || "N/A"}</StyledTableCell>
                        <StyledTableCell>{row.AcName || "N/A"}</StyledTableCell>
                        <StyledTableCell>{row.AcNo || "N/A"}</StyledTableCell>
                        <StyledTableCell>{row.AcType || "N/A"}</StyledTableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={settlementColumns.length} align="center">
                      No settlement data available
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDialog(false)}
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminDashboard>
  );
};

export default Earnings;